import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import { useLang } from "../contexts/LangContext";
import { withLang } from "../components/SeoHead";
import { ShareButtons } from "../components/ShareButtons";
import api from "../lib/api";
import { getLowPlacesThreshold, isLowPlaces } from "../lib/lowPlaces";
import { Users, Calendar } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fullUrl = (u) => (u && u.startsWith("/uploads/") ? `${BACKEND}/api${u}` : u);

export default function Stages() {
  const { t, lang, img, content } = useLang();
  const navigate = useNavigate();
  const [stages, setStages] = useState([]);
  const [cats, setCats] = useState([]);
  const [tab, setTab] = useState("all");

  const lowThreshold = getLowPlacesThreshold(content);

  useEffect(() => {
    api.get("/stages").then((r) => setStages(r.data)).catch(() => {});
    api.get("/stage-categories").then((r) => setCats(r.data || [])).catch(() => {});
  }, []);

  const tabs = [{ slug: "all", name_fr: t.common.all }, ...cats];
  const filtered = tab === "all" ? stages : stages.filter((s) => s.category === tab);
  const catName = (slug) => {
    const c = cats.find((x) => x.slug === slug);
    return c ? (c[`name_${lang}`] || c.name_fr) : "";
  };

  const goToActivity = (s) => {
    if (s.source === "product" && s.product_id) {
      navigate(withLang(lang, `/shop/${s.product_id}`));
    } else {
      navigate(withLang(lang, "/shop") + "?cat=stages");
    }
  };

  const fmtDate = (d) => new Date(d).toLocaleDateString(lang === "en" ? "en-GB" : lang, { day: "2-digit", month: "short" });

  return (
    <div data-testid="stages-page">
      <PageHeader eyebrow={t.stages.eyebrow} title={t.stages.title} lead={t.stages.lead}
        image={img("stages", "header", "https://images.unsplash.com/photo-1501771924607-209f42a6e7e4?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400")} />

      {/* Tabs */}
      <section className="pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
          <div className="flex gap-3 border-b border-slate-200 mb-12 flex-wrap" data-testid="stages-tabs">
            {tabs.map((c) => (
              <button
                key={c.slug}
                onClick={() => setTab(c.slug)}
                className={`px-6 py-4 text-sm uppercase tracking-[0.2em] border-b-2 transition-colors ${tab === c.slug ? "border-club-red text-club-red font-semibold" : "border-transparent text-slate-500 hover:text-navy"}`}
                data-testid={`tab-${c.slug}`}
              >
                {c[`name_${lang}`] || c.name_fr}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {filtered.length === 0 && (
              <div className="md:col-span-2 text-center text-slate-500 py-12 bg-sand border border-dashed border-slate-300" data-testid="stages-empty">
                {{ fr: "Aucun stage dans cette catégorie pour le moment.", nl: "Momenteel geen stages in deze categorie.", en: "No camps in this category at the moment." }[lang]}
              </div>
            )}
            {filtered.map((s) => {
              const hasPlaces = (s.places || 0) > 0;
              const left = (s.places || 0) - (s.places_taken || 0);
              const isClosed = s.stage_status === "closed";
              const isFull = isClosed || (hasPlaces && left <= 0);
              const isLowPlacesCard = isLowPlaces({ places: s.places, taken: s.places_taken, isFull, isClosed, threshold: lowThreshold });
              return (
                <article key={s.id} className="bg-white border border-slate-200 hover:border-club-red transition-colors group flex flex-col" data-testid={`stage-${s.id}`}>
                  <div className="aspect-video overflow-hidden bg-sand relative">
                    {s.image && <img src={fullUrl(s.image)} alt={s[`title_${lang}`]} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
                    <div className="absolute top-3 right-3">
                      <ShareButtons
                        compact
                        lang={lang}
                        title={s[`title_${lang}`]}
                        url={`${window.location.origin}${withLang(lang, s.source === "product" && s.product_id ? `/shop/${s.product_id}` : "/stages")}`}
                        testid={`share-stage-${s.id}`}
                      />
                    </div>
                    {isLowPlacesCard && (
                      <div className="absolute top-3 left-3 bg-club-red text-white text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 font-semibold shadow-lg animate-pulse" data-testid={`last-places-badge-${s.id}`}>
                        {t.stages.lastPlaces}
                      </div>
                    )}
                  </div>
                  <div className="p-8 flex-1 flex flex-col">
                    <div className="flex items-center gap-3 mb-3 flex-wrap">
                      <span className="text-[10px] uppercase tracking-[0.25em] bg-club-yellow/20 text-club-yellow-dark px-2 py-1 font-semibold" data-testid={`stage-cat-badge-${s.id}`}>
                        {catName(s.category) || s.category}
                      </span>
                      {s.age_min != null && s.age_max != null && (
                        <span className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-semibold">
                          {s.age_min} – {s.age_max} {t.stages.ages.toLowerCase()}
                        </span>
                      )}
                    </div>
                    <h3 className="font-serif text-2xl text-navy mb-3">{s[`title_${lang}`]}</h3>
                    <p className="text-sm text-slate-600 mb-5 flex-1">{s[`description_${lang}`]}</p>
                    <div className="grid grid-cols-2 gap-3 text-xs border-t border-slate-100 pt-4 mb-5">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1 flex items-center gap-1"><Calendar className="w-3 h-3" />{t.stages.duration}</div>
                        <div className="text-navy">
                          {s.start_date ? (<>{fmtDate(s.start_date)}{s.end_date && s.end_date !== s.start_date ? <> → {fmtDate(s.end_date)}</> : null}</>) : "—"}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1 flex items-center gap-1"><Users className="w-3 h-3" />{t.stages.places}</div>
                        <div className={isLowPlacesCard ? "text-club-red font-semibold" : "text-navy"}>
                          {isClosed ? (t.shop.stageClosed || t.stages.full) : hasPlaces ? (isFull ? t.stages.full : `${left} ${t.stages.placesLeft}`) : "—"}
                        </div>
                        {isFull && !isClosed && (s.waitlist_count || 0) > 0 && (
                          <div className="text-[11px] text-club-red mt-1 italic" data-testid={`waitlist-count-${s.id}`}>
                            {s.waitlist_count === 1 ? t.stages.waitlistOne : t.stages.waitlistMany.replace("{n}", s.waitlist_count)}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-end justify-between">
                      <div className="font-serif text-3xl text-navy">€ {(s.price || 0).toFixed(0)}</div>
                      {(() => {
                        const canWaitlist = s.source === "product" && isFull && !isClosed;
                        return (
                          <button
                            disabled={isFull && !canWaitlist}
                            onClick={() => goToActivity(s)}
                            className={`px-6 py-3 text-xs uppercase tracking-[0.25em] transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${canWaitlist ? "bg-club-red text-white hover:bg-navy" : "bg-navy text-white hover:bg-club-red"}`}
                            data-testid={canWaitlist ? `waitlist-stage-${s.id}` : `register-stage-${s.id}`}
                          >
                            {canWaitlist ? t.shop.waitlistBtn : t.stages.register}
                          </button>
                        );
                      })()}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
